// Passkey management (see `features/passkey-login.md`): the signed-in user's
// own passkeys — list, add (up to the 5-per-user cap) and remove. Opened from
// the admin layout's app bar and from the athlete surface's header, so every
// kind of account manages its passkeys in one shared dialog. Password and
// magic-link sign-in always remain, so removing the last passkey never locks
// anyone out.

import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import type { PasskeyInfo } from '../domain';
import { MAX_PASSKEYS_PER_USER } from '../domain';
import {
  addPasskeyCeremony,
  deleteMyPasskey,
  fetchMyPasskeys,
  isPasskeyCancelled,
  passkeysSupported,
} from '../data/passkeyClient';
import { useTranslation } from '../state/I18nContext';

interface PasskeysDialogProps {
  open: boolean;
  onClose: () => void;
}

export function PasskeysDialog({ open, onClose }: PasskeysDialogProps) {
  const { t, localeCode } = useTranslation();
  // null while the initial list is loading.
  const [passkeys, setPasskeys] = useState<PasskeyInfo[] | null>(null);
  const [limit, setLimit] = useState(MAX_PASSKEYS_PER_USER);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supported = passkeysSupported();

  const load = useCallback(async () => {
    try {
      const mine = await fetchMyPasskeys();
      setPasskeys(mine.passkeys);
      setLimit(mine.limit);
    } catch {
      setError(t('passkeys.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    if (open) {
      setPasskeys(null);
      setName('');
      setError(null);
      void load();
    }
  }, [open, load]);

  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await addPasskeyCeremony(name);
      setName('');
      await load();
    } catch (e: unknown) {
      // Backing out of the browser's passkey prompt is a non-event.
      if (!isPasskeyCancelled(e)) {
        setError(e instanceof Error ? e.message : t('passkeys.addFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteMyPasskey(id);
      await load();
    } catch {
      setError(t('passkeys.removeFailed'));
    } finally {
      setBusy(false);
    }
  };

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(localeCode, { dateStyle: 'medium' }),
    [localeCode],
  );
  const atCap = passkeys !== null && passkeys.length >= limit;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('passkeys.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('passkeys.description')}
          </Typography>
          <Alert severity="info">{t('passkeys.perDevice')}</Alert>
          {!supported && <Alert severity="warning">{t('passkeys.notSupported')}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          {passkeys === null && !error ? (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : passkeys !== null && passkeys.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('passkeys.empty')}
            </Typography>
          ) : passkeys !== null ? (
            <List dense disablePadding>
              {passkeys.map((passkey) => (
                <ListItem
                  key={passkey.id}
                  disableGutters
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={t('passkeys.remove')}
                      disabled={busy}
                      onClick={() => void remove(passkey.id)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <KeyOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={passkey.name || t('passkeys.unnamed')}
                    secondary={`${t('passkeys.addedOn', {
                      date: dateFormat.format(passkey.createdAt),
                    })} · ${
                      passkey.lastUsedAt === null
                        ? t('passkeys.neverUsed')
                        : t('passkeys.lastUsed', {
                            date: dateFormat.format(passkey.lastUsedAt),
                          })
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          ) : null}

          {passkeys !== null && (
            <Typography variant="caption" color="text.secondary">
              {t('passkeys.count', { count: passkeys.length, max: limit })}
            </Typography>
          )}

          {supported && (
            <Stack direction="row" spacing={1}>
              <TextField
                label={t('passkeys.nameLabel')}
                placeholder={t('passkeys.namePlaceholder')}
                size="small"
                fullWidth
                value={name}
                disabled={busy || atCap}
                onChange={(e) => setName(e.target.value)}
              />
              <Button
                variant="contained"
                startIcon={<FingerprintIcon />}
                disabled={busy || atCap || passkeys === null}
                onClick={() => void add()}
                sx={{ flexShrink: 0 }}
              >
                {busy ? t('passkeys.adding') : t('passkeys.add')}
              </Button>
            </Stack>
          )}
          {atCap && (
            <Typography variant="caption" color="text.secondary">
              {t('passkeys.limitReached', { max: limit })}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('admin.common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
